import { DeliveryUpdate } from "./delivery-update";
import { IUpdatedDeliveryResult } from "./interfaces/IUpdatedDeliveryResult";

// TODO: Put these into a central file for the other implementations to use.
interface Address {
    Company: string;
    Line1: string;
    Line2: string;
    Line3: string;
    City: string;
    Province: string;
    PostalCode: string;
    CountryIso3: string;
}

interface Variations {
    // Define the structure of variations if needed, otherwise leave it as an empty interface
}
// TODO: Put these into a central file for the other implementations to use.

export class OrderSummary {
    public static Init(): void {
        new OrderSummary();
    }

    private addressContainerDelivery = <HTMLUListElement>document.getElementById("address-container-delivery");
    private addressContainerStore = <HTMLUListElement>document.getElementById("address-container-store");
    private addressEditContainer = <HTMLUListElement>document.getElementById("address-edit-container");
    private addressPane = <HTMLDivElement>document.getElementById("address-container");
    private changeAddressLink = <HTMLAnchorElement>document.getElementById("change-delivery-address");
    private deliveryServicePane = <HTMLDivElement>document.getElementById("delivery-services-pane");
    private deliveryUpdater: DeliveryUpdate;
    private readonly formId = "summary-form";
    private readonly formSelector = "#" + this.formId;
    private form = document.getElementById(this.formId) as HTMLFormElement;

    private addressElementShipTo = <HTMLInputElement>document.getElementById("ShipTo");
    private addressElementCompany = <HTMLInputElement>document.getElementById("CompanyName");
    private addressElementLine1 = <HTMLInputElement>document.getElementById("AddressLine1");
    private addressElementLine2 = <HTMLInputElement>document.getElementById("AddressLine2");
    private addressElementDistrict = <HTMLInputElement>document.getElementById("AddressDistrict");
    private addressElementTown = <HTMLInputElement>document.getElementById("AddressTown");
    private addressElementCounty = <HTMLInputElement>document.getElementById("AddressCounty");
    private addressElementPostalCode = <HTMLInputElement>document.getElementById("AddressPostalCode");
    private addressElementCountryCode3 = <HTMLSelectElement>document.getElementById("AddressCountryCode3");

    constructor() {
        this.deliveryUpdater = DeliveryUpdate.Init(
            (deliveryTypeIsStore) => this.deliveryTypeUpdated(deliveryTypeIsStore),
            (update) => this.onOrderUpdated(update)
        );
        this.WireUpCalculateDelivery();
        this.WireUpAddressEditToggle();
        this.wireUpCountryChange();
        this.catchSubmit();
        this.MapLoqateFields();
        // If the customer navigated back they could have a cached page with the store selector hidden, so update the UI to match the current state.
        this.deliveryUpdater.UpdateUiForDeliveryType();

    }

    private catchSubmit(): void {
        this.form.addEventListener("submit", (e) => this.onFormSubmit(e));
    }

    private onFormSubmit(e: Event): void {
        if (!window.validationService.isValid(this.form)) {
            e.preventDefault();

            if (this.errorInAddressFields()) {
                this.ShowAddressEntryUi();
            }
            else {
                // Error must be in delivery selection fields.
                this.HideAddressEntryUi();
            }

            return;
        }

        if (this.topAddressPaneIsVisible()) {
            e.preventDefault();
            // Customer pressed enter when in one of the address entry form fields. Don't treat this as a submit, just switch to the delivery services.
            this.onCalculateDeliveryClicked(new MouseEvent("click"));
            return;
        }

        // Form OK, allow submit to go through.
        window.FormSubmitToggle.DisableForm(this.formSelector);
    }

    private errorInAddressFields(): boolean {
        let fieldWithError = this.addressEditContainer.querySelector(".field-validation-error");
        return !!fieldWithError;
    }

    private deliveryTypeUpdated(deliveryTypeIsStore: boolean): void {
        this.setDeliveryServiceAddressLabelVisibility(deliveryTypeIsStore);
    }

    private setAddressRequired(value: boolean): void {
        if (value) {
            (<HTMLInputElement>document.getElementById("AddressRequired")).value = "true";
        }
        else {
            (<HTMLInputElement>document.getElementById("AddressRequired")).value = "false";
        }
    }

    private WireUpAddressEditToggle(): void {
        this.changeAddressLink.addEventListener("click", (e) => this.onChangeAddressClicked(e));
    }

    private wireUpCountryChange(): void {
        let countryElement = <HTMLSelectElement>document.getElementById("AddressCountryCode3");
        countryElement.addEventListener('change', (e) => this.onCountryChanged());
    }

    private onCountryChanged(): void {
        let countryOption = this.selectedCountryOption();

        if (countryOption) {
            this.setFieldLabelsByCountryCode(countryOption.value);
        }
    }

    private WireUpCalculateDelivery(): void {
        let button = <HTMLButtonElement>document.getElementById("calculate-delivery");
        button.addEventListener("click", (e) => this.onCalculateDeliveryClicked(e));
    }

    private onCalculateDeliveryClicked(e: MouseEvent): void {
        e.preventDefault();

        if (!window.validationService.isValid(this.form)) {
            if (this.errorInAddressFields()) {
                this.ShowAddressEntryUi();
                return;
            }
        }

        // Address must be OK, load it into the read-only label for display in a moment.
        this.copyAddressToReadOnlyField();

        window.FormSubmitToggle.DisableForm(this.formSelector);

        // Request the new delivery services for the address, we'll get a callback when done.

        const countryCode3 = this.addressElementCountryCode3.value;
        const postalCode = this.addressElementPostalCode.value;

        this.deliveryUpdater.RequestDeliveryUpdate(countryCode3, postalCode);
    }

    private copyAddressToReadOnlyField(): void {
        let address = [];

        address.push(this.addressElementShipTo.value);
        address.push(this.addressElementCompany.value);
        address.push(this.addressElementLine1.value);
        address.push(this.addressElementLine2.value);
        address.push(this.addressElementDistrict.value);
        address.push(this.addressElementTown.value);
        address.push(this.addressElementCounty.value);
        address.push(this.addressElementPostalCode.value);

        let countryOption = this.selectedCountryOption();
        if (countryOption) {
            address.push(countryOption.text);
        }

        // Join all address lines into a single string, ignoring blanks.
        let formattedAddress = address.filter(function (val) { return val; }).join("<br />");

        (<HTMLSpanElement>document.getElementById("customer-delivery-address-label")).innerHTML = formattedAddress;
    }

    private selectedCountryOption(): HTMLOptionElement {
        let countryDropdown = <HTMLSelectElement>document.getElementById("AddressCountryCode3");

        if (countryDropdown && countryDropdown.selectedIndex >= 0) {
            return countryDropdown.options[countryDropdown.selectedIndex];
        }

        return null;
    }

    // Called from the delivery updater javascript when new delivery options have been returned from the server.
    private onOrderUpdated(update: IUpdatedDeliveryResult): void {
        window.FormSubmitToggle.EnableForm(this.formSelector);
        this.HideAddressEntryUi();
        this.updateStoreAddress(update);
    }

    private updateStoreAddress(update: IUpdatedDeliveryResult) {
        let htmlAddress;
        let showStore = (update.storeId && update.storeAddress);

        if (showStore) {
            htmlAddress = update.storeAddress.join("<br />");
        }
        else {
            htmlAddress = "No store currently selected.";
        }

        let storeDeliveryAddressElement = document.getElementById("store-delivery-address-label");
        if (storeDeliveryAddressElement) {
            storeDeliveryAddressElement.innerHTML = htmlAddress;
        }
    }

    private onChangeAddressClicked(e: MouseEvent): void {
        e.preventDefault();

        if (this.topAddressPaneIsVisible()) {
            this.HideAddressEntryUi();
        }
        else {
            this.ShowAddressEntryUi();
        }
    }

    private topAddressPaneIsVisible(): boolean {
        return !this.addressPane.classList.contains("hidden");
    }

    private ShowAddressEntryUi(): void {
        this.addressPane.classList.remove("hidden");
        this.addressEditContainer.classList.remove("hidden");
        this.deliveryServicePane.classList.add("hidden");
        this.addressContainerDelivery.classList.add("hidden");
        this.addressContainerStore.classList.add("hidden");
        this.setAddressRequired(true);
    }

    /**
     * Hides the address form fields, shows the delivery address as read only text elements
     * and shows the delivery type selection UI.
     */
    private HideAddressEntryUi(): void {
        this.addressPane.classList.add("hidden");
        this.addressEditContainer.classList.add("hidden");
        this.deliveryServicePane.classList.remove("hidden");

        this.setDeliveryServiceAddressLabelVisibility(this.deliveryUpdater.selectedDeliveryTypeIsStore());
    }

    private setDeliveryServiceAddressLabelVisibility(deliveryTypeIsStore: boolean): void {
        if (deliveryTypeIsStore) {
            this.addressContainerDelivery.classList.add("hidden");
            this.addressContainerStore.classList.remove("hidden");
            this.setAddressRequired(false);
        }
        else {
            this.addressContainerDelivery.classList.remove("hidden");
            this.addressContainerStore.classList.add("hidden");
            this.setAddressRequired(true);
        }
    }

    private MapLoqateFields(): void {
        let options = {
            key: "TM62-NA98-RK41-AU82",
            bar: { visible: true },
            GeoLocationEnabled: 'false',
            GeoLocationRadius: 50,
            GeoLocationMaxItems: 10,
            setCountryByIP: true,
            suppressAutocomplete: false
        };
        let fields = [
            { element: "address-lookup", field: "Line1", mode: pca.fieldMode.SEARCH }
        ];

        let control = new pca.Address(fields, options);
        control.listen("populate", (address: Address, variations: Variations) => this.loquatePopulatedAsync(address, variations));
    }

    private async loquatePopulatedAsync(address: Address, variations: Variations): Promise<void> {
        (<HTMLInputElement>document.getElementById("address-lookup")).value = "";
        this.addressElementCompany.value = address.Company;
        this.addressElementLine1.value = address.Line1;
        this.addressElementLine2.value = address.Line2;
        this.addressElementDistrict.value = address.Line3;
        this.addressElementTown.value = address.City;
        this.addressElementCounty.value = address.Province;
        this.addressElementPostalCode.value = address.PostalCode;
        this.addressElementCountryCode3.value = address.CountryIso3;

        this.setFieldLabelsByCountryCode(address.CountryIso3);

        let addressLookupContainer = document.getElementById("address-lookup-container");
        addressLookupContainer.classList.add("hidden");

        // Revalidate the form now the address has been populated.
        await window.validationService.validateForm(this.form);
    }

    private setFieldLabelsByCountryCode(countryIso3: string): void {
        let countyElement = <HTMLInputElement>document.querySelector("label[for=AddressCounty]");
        let stateRequirementElement = <HTMLInputElement>document.getElementById("StateRequired");

        if (countryIso3 === "USA" || countryIso3 === "CAN") {
            countyElement.innerText = "State:";
            stateRequirementElement.value = "true";
        }
        else {
            countyElement.innerText = "County:";
            stateRequirementElement.value = "false";
        }
    }
}

if (document.body.dataset.pageInit == "ordersummary") {
    OrderSummary.Init();
}
